import { useCallback, useEffect, useState } from 'react';
import { useCanvasStore } from '../store/canvasStore';
import type { CanvasHistoryState } from '../types';

type StoreState = ReturnType<typeof useCanvasStore.getState>;
type CanvasImage = StoreState['canvasImages'][number];

const useCanvasHistory = () => {
  const store = useCanvasStore();
  const [history, setHistory] = useState<CanvasHistoryState[]>([]);
  const [future, setFuture] = useState<CanvasHistoryState[]>([]);

  const createSnapshot = useCallback((): CanvasHistoryState => {
    const { texts, logoProps, logoImage, bgImage, backgroundColor, canvasImages } = store;

    return {
      texts: structuredClone(texts),
      logoProps: { ...logoProps },
      logoImage,
      bgImage,
      backgroundColor,
      canvasImages: structuredClone(canvasImages),
      timestamp: Date.now(),
    };
  }, [store]);

  const saveHistory = useCallback(() => {
    const snapshot = createSnapshot();
    setHistory((prev) => [...prev.slice(-19), snapshot]);
    setFuture([]);
  }, [createSnapshot]);

  const undo = useCallback(() => {
    if (history.length === 0) return;

    const currentState = createSnapshot();
    const newHistory = [...history];
    const previousState = newHistory.pop();

    if (!previousState) return;

    setFuture((prev) => [currentState, ...prev]);

    store.setTexts(previousState.texts);
    store.setLogoProps(previousState.logoProps);
    store.setLogoImage(previousState.logoImage);
    store.setBgImage(previousState.bgImage);
    store.setBackgroundColor(previousState.backgroundColor);

    useCanvasStore.setState({
      canvasImages: previousState.canvasImages as CanvasImage[],
    });

    setHistory(newHistory);
  }, [history, createSnapshot, store]);

  const redo = useCallback(() => {
    if (future.length === 0) return;

    const currentState = createSnapshot();
    const newFuture = [...future];
    const nextState = newFuture.shift();

    if (!nextState) return;

    setHistory((prev) => [...prev, currentState]);

    store.setTexts(nextState.texts);
    store.setLogoProps(nextState.logoProps);
    store.setLogoImage(nextState.logoImage);
    store.setBgImage(nextState.bgImage);
    store.setBackgroundColor(nextState.backgroundColor);

    useCanvasStore.setState({
      canvasImages: nextState.canvasImages as CanvasImage[],
    });

    setFuture(newFuture);
  }, [future, createSnapshot, store]);

  useEffect(() => {
    return () => {
      setHistory([]);
      setFuture([]);
    };
  }, []);

  return {
    history,
    future,
    saveHistory,
    undo,
    redo,
    canUndo: history.length > 0,
    canRedo: future.length > 0,
  };
};

export default useCanvasHistory;
